import { useState } from 'react';
import PageHeader from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import {
  useCategories, useManufacturers,
  useCreateCategory, useUpdateCategory, useDeleteCategory,
  useCreateManufacturer, useUpdateManufacturer, useDeleteManufacturer,
} from '@/hooks/useSettings';
import {
  useAdminSites, useCreateSite, useUpdateSite, useDeleteSite,
} from '@/hooks/useAdmin';
import { toast } from 'sonner';
import type { Category, Manufacturer, Site } from '@/types/models';

function EditableList<T extends { id: string; name?: string; is_active: boolean }>({
  items,
  onAdd,
  onToggle,
  onRename,
  placeholder,
}: {
  items: T[];
  onAdd: (name: string) => void;
  onToggle: (id: string) => void;
  onRename: (id: string, name: string) => void;
  placeholder: string;
}) {
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex gap-2">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={placeholder}
            onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) { onAdd(newName.trim()); setNewName(''); } }}
          />
          <Button onClick={() => { if (newName.trim()) { onAdd(newName.trim()); setNewName(''); } }} disabled={!newName.trim()} className="gap-1.5">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
        <div className="space-y-1">
          {items.map((item) => {
            const displayName = (item as any).name ?? (item as any).company ?? '';
            return (
              <div key={item.id} className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50">
                {editingId === item.id ? (
                  <div className="flex items-center gap-2 flex-1">
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-8 text-sm" onKeyDown={(e) => { if (e.key === 'Enter') { onRename(item.id, editName.trim()); setEditingId(null); } }} autoFocus />
                    <Button variant="ghost" size="sm" onClick={() => { onRename(item.id, editName.trim()); setEditingId(null); }} className="h-8 w-8 p-0"><Check className="h-4 w-4 text-status-complete" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setEditingId(null)} className="h-8 w-8 p-0"><X className="h-4 w-4 text-muted-foreground" /></Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm ${item.is_active ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{displayName}</span>
                      {!item.is_active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingId(item.id); setEditName(displayName); }} className="h-8 w-8 p-0"><Pencil className="h-3.5 w-3.5 text-muted-foreground" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => onToggle(item.id)} className="h-8 w-8 p-0"><Trash2 className={`h-3.5 w-3.5 ${item.is_active ? 'text-destructive' : 'text-status-complete'}`} /></Button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminSettingsPage() {
  const { data: rawCategories = [], isError: catErr } = useCategories();
  const { data: rawManufacturers = [], isError: mfgErr } = useManufacturers();
  const { data: rawSites = [], isError: siteErr } = useAdminSites();
  

  const categories = Array.isArray(rawCategories) ? rawCategories : [];
  const manufacturers = Array.isArray(rawManufacturers) ? rawManufacturers : [];
  const sites = Array.isArray(rawSites) ? rawSites : [];
  

  const createCat = useCreateCategory();
  const updateCat = useUpdateCategory();
  const createMfg = useCreateManufacturer();
  const updateMfg = useUpdateManufacturer();
  const createSite = useCreateSite();
  const updateSite = useUpdateSite();

  return (
    <>
      <PageHeader title="Admin Settings" description="Manage categories, manufacturers, and sites" />
      <Tabs defaultValue="categories" className="mt-6">
        <TabsList>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="manufacturers">Manufacturers</TabsTrigger>
          <TabsTrigger value="sites">Sites</TabsTrigger>
          
        </TabsList>

        <TabsContent value="categories" className="mt-4">
          <EditableList
            items={categories}
            placeholder="New category name..."
            onAdd={(name) => createCat.mutate(name, { onSuccess: (c) => toast.success(`Added: ${c.name}`) })}
            onToggle={(id) => { const c = categories.find(x => x.id === id); if (c) updateCat.mutate({ id, updates: { name: c.name, is_active: !c.is_active } }); }}
            onRename={(id, name) => updateCat.mutate({ id, updates: { name } }, { onSuccess: () => toast.success('Updated') })}
          />
        </TabsContent>

        <TabsContent value="manufacturers" className="mt-4">
          <EditableList
            items={manufacturers}
            placeholder="New manufacturer name..."
            onAdd={(name) => createMfg.mutate(name, { onSuccess: (m) => toast.success(`Added: ${m.name}`) })}
            onToggle={(id) => { const m = manufacturers.find(x => x.id === id); if (m) updateMfg.mutate({ id, updates: { name: m.name, is_active: !m.is_active } }); }}
            onRename={(id, name) => updateMfg.mutate({ id, updates: { name } }, { onSuccess: () => toast.success('Updated') })}
          />
        </TabsContent>

        <TabsContent value="sites" className="mt-4">
          <SitesTab sites={sites} createSite={createSite} updateSite={updateSite} />
        </TabsContent>

      </Tabs>
    </>
  );
}

function SitesTab({ sites, createSite, updateSite }: { sites: Site[]; createSite: any; updateSite: any }) {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');

  const add = () => {
    if (!name.trim()) return;
    createSite.mutate({ name: name.trim(), address, city, state, is_active: true }, {
      onSuccess: () => { setName(''); setAddress(''); setCity(''); setState(''); toast.success('Site added'); },
    });
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
          <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address" />
          <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
          <Input value={state} onChange={(e) => setState(e.target.value)} placeholder="State" />
        </div>
        <Button onClick={add} disabled={!name.trim()} className="gap-1.5"><Plus className="h-4 w-4" /> Add Site</Button>
        <div className="space-y-1">
          {sites.map((s) => (
            <div key={s.id} className="flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50">
              <div>
                <span className={`text-sm font-medium ${s.is_active ? 'text-foreground' : 'text-muted-foreground line-through'}`}>{s.name}</span>
                <span className="ml-2 text-xs text-muted-foreground">{s.city}, {s.state}</span>
                {!s.is_active && <Badge variant="outline" className="ml-2 text-[10px]">Inactive</Badge>}
              </div>
              <Button variant="ghost" size="sm" onClick={() => updateSite.mutate({ id: s.id, data: { is_active: !s.is_active } })} className="h-8 w-8 p-0">
                <Trash2 className={`h-3.5 w-3.5 ${s.is_active ? 'text-destructive' : 'text-status-complete'}`} />
              </Button>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

